import './applicantIncomeDetailsPage.scss';

import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import red from '@mui/material/colors/red';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import TextField from '@mui/material/TextField';
import { isEmpty, toString, uniqueId } from 'lodash';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/shared_new/NavigationPane/NavigationPane';
import { incomeFrequencyOptions } from '../../../store/applicantStore/constants/incomeFrequencyOptions';
import { incomeTypeOptions } from '../../../store/applicantStore/constants/incomeTypeOptions';
import { IncomeTypeEnum } from '../../../store/applicantStore/enums/IncomeTypeEnum';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';
import { inputEventHandler } from '../../../utils/component/inputEventHandler';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();
  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: 'onBlur',
    criteriaMode: 'firstError',
    delayError: 200,
  });

  const {
    additionalIncomeAmount,
    additionalIncomeDurationMonths,
    additionalIncomeDurationYears,
    additionalIncomeEmployerName,
    additionalIncomeFrequency,
    additionalIncomeJobTitle,
    additionalIncomeType,
    isSyncingIncome,
    syncingIncomeFailedMessage,
    setAdditionalIncomeAmount,
    setAdditionalIncomeDurationMonths,
    setAdditionalIncomeDurationYears,
    setAdditionalIncomeEmployerName,
    setAdditionalIncomeFrequency,
    setAdditionalIncomeJobTitle,
    setAdditionalIncomeType,
    syncIncome,
  } = useApplicantStore();

  const previousStep = (): void => {
    navigate(previous());
  };

  const incomeTypeValidation = {
    required: t('validation.incomeType.required'),
  };

  const incomeFrequencyValidation = {
    required: t('validation.incomeFrequency.required'),
  };

  const incomeAmountValidation = {
    setValueAs: (value: string): string =>
      inputEventHandler.currency.parser(value),
    required: t('validation.incomeAmount.required'),
    min: {
      value: 0,
      message: t('validation.incomeAmount.min', { min: '$0' }),
    },
  };

  const incomeEmployerNameValidation = {
    required: t('validation.employerName.required'),
  };

  const incomeJobTitleValidation = {
    required: t('validation.jobTitle.required'),
  };

  const incomeDurationYearsValidation = {
    required: t('validation.durationYears.required'),
  };

  const incomeDurationMonthsValidation = {
    required: t('validation.durationMonths.required'),
  };

  const showSecondaryEmploymentFields =
    watch('incomeTypeValue', additionalIncomeType) ===
    IncomeTypeEnum.SECONDARY_EMPLOYMENT;

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      incomeTypeValue,
      incomeDurationMonthsValue,
      incomeDurationYearsValue,
      incomeEmployerNameValue,
      incomeFrequencyValue,
      incomeJobTitleValue,
      incomeAmountValue,
    } = values;

    if (!incomeTypeValue || !incomeFrequencyValue || !incomeAmountValue) {
      return;
    }

    setAdditionalIncomeAmount(incomeAmountValue);
    setAdditionalIncomeFrequency(incomeFrequencyValue);
    setAdditionalIncomeType(incomeTypeValue);

    if (showSecondaryEmploymentFields) {
      setAdditionalIncomeDurationMonths(incomeDurationMonthsValue);
      setAdditionalIncomeDurationYears(incomeDurationYearsValue);
      setAdditionalIncomeEmployerName(incomeEmployerNameValue);
      setAdditionalIncomeJobTitle(incomeJobTitleValue);
    } else {
      setAdditionalIncomeDurationMonths(null);
      setAdditionalIncomeDurationYears(null);
      setAdditionalIncomeEmployerName(null);
      setAdditionalIncomeJobTitle(null);
    }

    if (isDirty) {
      const synced = await syncIncome();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="applicant-income-details-page" sx={theme.pageLayout}>
        <header data-testid={locator.pages.applicantIncomeDetails.header.root}>
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.applicantIncomeDetails.header.previousStepButton
            }
            type="button"
            onClick={previousStep}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={
              locator.pages.applicantIncomeDetails.header.drawerButton
            }
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={locator.pages.applicantIncomeDetails.progress.root}
            value={useWorkflowStore.getState().computed.progress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={locator.pages.applicantIncomeDetails.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applicantIncomeDetails.form.h1}>
                {t('ApplicantIncomeDetailsPage.title')}
              </h1>
              <h2 data-testid={locator.pages.applicantIncomeDetails.form.h2}>
                {t('ApplicantIncomeDetailsPage.subtitle')}
              </h2>

              {!isEmpty(syncingIncomeFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.applicantIncomeDetails.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingIncomeFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <TextField
                    {...register('incomeTypeValue', incomeTypeValidation)}
                    SelectProps={{
                      IconComponent: ExpandMoreRoundedIcon,
                    }}
                    error={!isEmpty(errors.incomeTypeValue)}
                    inputProps={{
                      'data-testid':
                        locator.pages.applicantIncomeDetails.form
                          .incomeTypeSelect,
                    }}
                    label={t('ApplicantIncomeDetailsPage.incomeType')}
                    value={watch('incomeTypeValue', additionalIncomeType || '')}
                    fullWidth
                    select
                  >
                    <MenuItem value="" disabled>
                      {t('ApplicantIncomeDetailsPage.incomeType')}
                    </MenuItem>

                    {incomeTypeOptions.map(({ name, value }) => {
                      return (
                        <MenuItem key={value} value={value}>
                          {name}
                        </MenuItem>
                      );
                    })}
                  </TextField>

                  {!isEmpty(errors.incomeTypeValue) && (
                    <span
                      data-testid={
                        locator.pages.applicantIncomeDetails.form
                          .incomeTypeSelectError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.incomeTypeValue.message)}
                    </span>
                  )}
                </div>

                {showSecondaryEmploymentFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register(
                        'incomeEmployerNameValue',
                        incomeEmployerNameValidation,
                      )}
                      error={!isEmpty(errors.employerNameValue)}
                      inputProps={{
                        'data-testid':
                          locator.pages.applicantIncomeDetails.form
                            .employerNameInput,
                      }}
                      label={t('ApplicantIncomeDetailsPage.employerName')}
                      value={watch(
                        'incomeEmployerNameValue',
                        additionalIncomeEmployerName || '',
                      )}
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.incomeEmployerNameValue) && (
                      <span
                        data-testid={
                          locator.pages.applicantIncomeDetails.form
                            .employerNameInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.incomeEmployerNameValue.message)}
                      </span>
                    )}
                  </div>
                )}

                {showSecondaryEmploymentFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register(
                        'incomeJobTitleValue',
                        incomeJobTitleValidation,
                      )}
                      error={!isEmpty(errors.jobTitleValue)}
                      inputProps={{
                        'data-testid':
                          locator.pages.applicantIncomeDetails.form
                            .jobTitleInput,
                      }}
                      label={t('ApplicantIncomeDetailsPage.jobTitle')}
                      value={watch(
                        'incomeJobTitleValue',
                        additionalIncomeJobTitle || '',
                      )}
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.incomeJobTitleValue) && (
                      <span
                        data-testid={
                          locator.pages.applicantIncomeDetails.form
                            .jobTitleInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.incomeJobTitleValue.message)}
                      </span>
                    )}
                  </div>
                )}

                {showSecondaryEmploymentFields && (
                  <div
                    className="inline-inputs"
                    style={{ marginTop: '1.5rem' }}
                  >
                    <div className="input-half">
                      <TextField
                        {...register(
                          'incomeDurationYearsValue',
                          incomeDurationYearsValidation,
                        )}
                        SelectProps={{
                          IconComponent: ExpandMoreRoundedIcon,
                        }}
                        error={!isEmpty(errors.durationYearsValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.applicantIncomeDetails.form
                              .yearSelect,
                        }}
                        label={t('ApplicantIncomeDetailsPage.durationYears')}
                        value={watch(
                          'incomeDurationYearsValue',
                          additionalIncomeDurationYears,
                        )}
                        fullWidth
                        select
                      >
                        <MenuItem value="" disabled>
                          {t('ApplicantIncomeDetailsPage.durationYears')}
                        </MenuItem>

                        {Array.from({ length: 11 }, (_, index) => {
                          return (
                            <MenuItem
                              key={uniqueId(`duration-years-${index}`)}
                              value={index}
                            >
                              {index}
                            </MenuItem>
                          );
                        })}
                      </TextField>

                      {!isEmpty(errors.incomeDurationYearsValue) && (
                        <span
                          data-testid={
                            locator.pages.applicantIncomeDetails.form
                              .yearSelectError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.incomeDurationYearsValue.message)}
                        </span>
                      )}
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register(
                          'incomeDurationMonthsValue',
                          incomeDurationMonthsValidation,
                        )}
                        SelectProps={{
                          IconComponent: ExpandMoreRoundedIcon,
                        }}
                        error={!isEmpty(errors.durationMonthsValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.applicantIncomeDetails.form
                              .monthSelect,
                        }}
                        label={t('ApplicantIncomeDetailsPage.durationMonths')}
                        value={watch(
                          'incomeDurationMonthsValue',
                          additionalIncomeDurationMonths,
                        )}
                        fullWidth
                        select
                      >
                        <MenuItem value="" disabled>
                          {t('ApplicantIncomeDetailsPage.durationMonths')}
                        </MenuItem>

                        {Array.from({ length: 12 }, (_, index) => {
                          return (
                            <MenuItem
                              key={uniqueId(`duration-months-${index}`)}
                              value={index}
                            >
                              {index}
                            </MenuItem>
                          );
                        })}
                      </TextField>

                      {!isEmpty(errors.incomeDurationMonthsValue) && (
                        <span
                          data-testid={
                            locator.pages.applicantIncomeDetails.form
                              .monthSelectError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.incomeDurationMonthsValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register(
                      'incomeFrequencyValue',
                      incomeFrequencyValidation,
                    )}
                    SelectProps={{
                      IconComponent: ExpandMoreRoundedIcon,
                    }}
                    error={!isEmpty(errors.incomeFrequencyValue)}
                    inputProps={{
                      'data-testid':
                        locator.pages.applicantIncomeDetails.form
                          .frequencySelect,
                    }}
                    label={t('ApplicantIncomeDetailsPage.incomeFrequency')}
                    value={watch(
                      'incomeFrequencyValue',
                      additionalIncomeFrequency || '',
                    )}
                    fullWidth
                    select
                  >
                    <MenuItem value="" disabled>
                      {t('ApplicantIncomeDetailsPage.incomeFrequency')}
                    </MenuItem>

                    {incomeFrequencyOptions.map(({ name, value }) => {
                      return (
                        <MenuItem key={value} value={value}>
                          {name}
                        </MenuItem>
                      );
                    })}
                  </TextField>

                  {!isEmpty(errors.incomeFrequencyValue) && (
                    <span
                      data-testid={
                        locator.pages.applicantIncomeDetails.form
                          .frequencySelectError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.incomeFrequencyValue.message)}
                    </span>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register('incomeAmountValue', incomeAmountValidation)}
                    defaultValue={inputEventHandler.currency.formatter(
                      additionalIncomeAmount,
                    )}
                    error={!isEmpty(errors.incomeAmountValue)}
                    inputProps={{
                      'data-testid':
                        locator.pages.applicantIncomeDetails.form.amountInput,
                      inputMode: 'numeric',
                    }}
                    label={t('ApplicantIncomeDetailsPage.incomeAmount')}
                    type="text"
                    variant="outlined"
                    fullWidth
                    onBlur={inputEventHandler.currency.blur(
                      'incomeAmountValue',
                      setValue,
                      trigger,
                    )}
                    onChange={inputEventHandler.currency.change(
                      'incomeAmountValue',
                      setValue,
                    )}
                    onFocus={inputEventHandler.currency.focus(
                      'incomeAmountValue',
                      setValue,
                    )}
                    onKeyDown={inputEventHandler.number.keydown}
                    onPasteCapture={inputEventHandler.number.pasteCapture}
                  />

                  {!isEmpty(errors.incomeAmountValue) && (
                    <span
                      data-testid={
                        locator.pages.applicantIncomeDetails.form
                          .amountInputError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.incomeAmountValue.message)}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <LoadingButton
                  color="primary"
                  data-testid={
                    locator.pages.applicantIncomeDetails.form.submitButton
                  }
                  disabled={!isValid}
                  loading={isSyncingIncome}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('ApplicantIncomeDetailsPage.continue')}
                </LoadingButton>
              </div>
            </div>
            <footer
              data-testid={locator.pages.applicantIncomeDetails.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantIncomeDetailsPage';
